import type { IssueStatus } from '../../types';
import { STATUS_LABELS } from '../../types';
import type { TaskDetail as Detail, DetailCard, DetailWaiting } from '../../portfolio-detail';

/**
 * THE TASK DETAIL DRAWER — a project row on the roll-up opens to reveal the
 * board's real cards in place, so the weekly meeting reads task detail without
 * leaving for the board. Faithful to the approved mock
 * (dev-preview/portfolio/view-portfolio-drill.html): the `kc` card shape from
 * the signed-off drill-down, brought inline and given the one line the meeting
 * reads out — each card's latest update — with what it is waiting on and why
 * folded in beneath it. Renders from a pre-derived `TaskDetail` (portfolio-
 * detail.ts) alone; the cards were already loaded for the roll-up.
 */

import { MONTHS } from '../../when';
const DAY_MS = 86_400_000;

/** The dot colour beside a status group — the app's status palette. */
const STATUS_DOT: Record<IssueStatus, string> = {
  backlog: 'var(--s-backlog)',
  todo: 'var(--s-todo)',
  inprogress: 'var(--s-inprogress)',
  staging: 'var(--s-staging)',
  inreview: 'var(--s-inreview)',
  done: 'var(--s-done)',
  wontdo: 'var(--s-wontdo)',
};

/** A `YYYY-MM-DD` → "20 Sep". */
function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${d} ${MONTHS[m - 1]}`;
}

/** A ms day → "31 Aug", for the "since" date on a blocker. */
function fmtMs(ms: number): string {
  const dt = new Date(ms);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]}`;
}

/** The wtag colour modifier — only these four kinds carry one (as in the roll-up). */
function wtagMod(type: DetailWaiting['type']): string {
  return type === 'person' || type === 'decision' || type === 'external' || type === 'parked' ? type : '';
}

/** The short kind word inside the pill. */
function wtagLabel(type: DetailWaiting['type']): string {
  if (!type) return 'Waiting';
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/** The banner sentence — a bold lead built from the card's own blocker, never
 *  saying more than the fields know. A decision names the call; everything else
 *  names who or what the card waits on. */
function bannerLead(w: DetailWaiting): string {
  // Strip any leading "Waiting on …" the free-text label already carries, so the
  // prefix below never doubles ("Waiting on Waiting on …").
  const raw = w.label && w.label !== 'something' ? w.label.replace(/^waiting on\s+/i, '') : '';
  const label = raw.trim();
  if (w.type === 'decision') return label ? `Waiting on a decision — ${label}` : 'Waiting on a decision';
  return label ? `Waiting on ${label}` : 'Waiting on something';
}

function Card({ c, now, onOpen }: { c: DetailCard; now: number; onOpen: () => void }) {
  const dueText = c.dueDay
    ? `due ${fmtDay(c.dueDay)}${c.overdueDays > 0 ? ` · ${c.overdueDays}d over` : ''}`
    : '';
  const w = c.waiting;
  const sinceText =
    w && w.days != null ? `since ${fmtMs(now - w.days * DAY_MS)} · ${w.days}d` : null;
  return (
    <div
      className="kc kc-open"
      role="button"
      tabIndex={0}
      aria-label={`Open ${c.ref} — ${c.title}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="kc-top">
        <span className="kc-ref">{c.ref}</span>
        <span className="kc-title">{c.title}</span>
        {c.who ? <span className="kc-owner">{c.who}</span> : <span className="kc-owner none">Unassigned</span>}
        {c.dueDay ? (
          <>
            <span className="kc-sep">·</span>
            <span className={`kc-due${c.overdueDays > 0 ? ' over' : ''}`}>{dueText}</span>
          </>
        ) : null}
        {/* A real status move in the window — the honest "moved this week", naming
            the column it entered and the day; amber when it went backwards. */}
        {c.moved ? (
          <span className={`kc-moved${c.moved.back ? ' back' : ''}`}>
            <span className="g" />{STATUS_LABELS[c.moved.toStatus]} · {fmtMs(c.moved.at)}
          </span>
        ) : null}
      </div>
      <div className={`kc-upd${c.update ? '' : ' none'}`}>
        {c.update ? (
          <>
            <span className="lbl">Latest</span>
            {c.update}
          </>
        ) : (
          'No update yet.'
        )}
      </div>
      {w ? (
        <div className={`kc-banner${wtagMod(w.type) ? ` ${wtagMod(w.type)}` : ''}`}>
          <span className={`wtag${wtagMod(w.type) ? ` ${wtagMod(w.type)}` : ''}`}>{wtagLabel(w.type)}</span>
          <span className="bt">{bannerLead(w)}</span>
          {sinceText ? <span className={`since${w.days != null && w.days > 14 ? ' old' : ''}`}>{sinceText}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

/** The drawer. `resolveName` turns each card's raw assignee into a shown name,
 *  as the roll-up does for owners — so a card never shows a raw address. */
export default function TaskDetail({
  detail,
  now,
  resolveName,
  boardId,
  onOpenCard,
}: {
  detail: Detail;
  now: number;
  resolveName: (who: string) => string;
  /** The board these cards sit on — so a click opens the right card in place. */
  boardId: string;
  /** Open a card's lean detail panel, by board + card. */
  onOpenCard: (projectId: string, cardId: string) => void;
}) {
  const empty = detail.total === 0;
  if (empty) {
    return (
      <div className="pfd">
        <div className="pfd-empty">No cards on this board yet.</div>
      </div>
    );
  }
  const cards = detail.groups.map((g) => ({
    ...g,
    cards: g.cards.map((c) => ({ ...c, who: c.who ? resolveName(c.who) : '' })),
  }));
  return (
    <div className="pfd">
      {cards.length ? <p className="pfd-head">Cards on this board · grouped by status</p> : null}
      {cards.map((g) => (
        <div className="pfd-grp" key={g.status}>
          <div className="pfd-glabel">
            <span className="dot" style={{ background: STATUS_DOT[g.status] }} />
            {g.label} <span className="n">{g.cards.length}</span>
          </div>
          <div className="pfd-cards">
            {g.cards.map((c) => (
              <Card key={c.id} c={c} now={now} onOpen={() => onOpenCard(boardId, c.id)} />
            ))}
          </div>
        </div>
      ))}
      {detail.doneTitles.length ? (
        <div className="pfd-done">
          Done this cycle · {detail.doneTitles.length} {detail.doneTitles.length === 1 ? 'card' : 'cards'} — {detail.doneTitles.join(', ')}.
        </div>
      ) : detail.doneCount ? (
        <div className="pfd-done">{detail.doneCount} done.</div>
      ) : null}
    </div>
  );
}
